import React, { useCallback, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";
import { get } from "lodash";

import { toast } from "react-toastify";
import { useHistory, useParams } from "react-router-dom";
import { Controller, FormProvider, useForm } from "react-hook-form";

import {
  QUERY_ENTITY_OPENING_HOURS,
  QUERY_MINEW_FILTERED,
} from "../../../../../config/graphql/query";
import {
  UPDATE_OPENING_HOURS,
  DELETE_OPENING_HOURS,
  RESTART_MINEW,
  CREATE_OPENING_HOURS,
  CREATE_ENTITY_PROPERTIES,
} from "../../../../../config/graphql/mutation";

const GAMETOWN_TEMPLATES = ["Gametown"];
const DAILY_TEMPLATES: Record<string, LocationType> = {
  "Leven-in-zeebad": "leven_in_zeebad",
  "Aqua Loop": "aqua_loop",
};

const DAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

const DAY_LABELS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

type DayKey = typeof DAY_KEYS[number];

type DayFormValues = {
  closed: boolean;
  splitDay: boolean;
  slots: { start: string; end: string }[];
  staffPresent: boolean;
  staffSlots: { start: string; end: string }[];
};

type FormValues = {
  title: string;
  monday: DayFormValues;
  tuesday: DayFormValues;
  wednesday: DayFormValues;
  thursday: DayFormValues;
  friday: DayFormValues;
  saturday: DayFormValues;
  sunday: DayFormValues;
};

function getLocationType(template: string): LocationType {
  if (GAMETOWN_TEMPLATES.includes(template)) return "gametown";
  return DAILY_TEMPLATES[template] ?? "default";
}

const defaultDay: DayFormValues = {
  closed: false,
  splitDay: false,
  slots: [{ start: "", end: "" }],
  staffPresent: true,
  staffSlots: [{ start: "", end: "" }],
};

const defaultValues: FormValues = {
  title: "",
  monday: {
    ...defaultDay,
    slots: [{ start: "", end: "" }],
    staffSlots: [{ start: "", end: "" }],
  },
  tuesday: {
    ...defaultDay,
    slots: [{ start: "", end: "" }],
    staffSlots: [{ start: "", end: "" }],
  },
  wednesday: {
    ...defaultDay,
    slots: [{ start: "", end: "" }],
    staffSlots: [{ start: "", end: "" }],
  },
  thursday: {
    ...defaultDay,
    slots: [{ start: "", end: "" }],
    staffSlots: [{ start: "", end: "" }],
  },
  friday: {
    ...defaultDay,
    slots: [{ start: "", end: "" }],
    staffSlots: [{ start: "", end: "" }],
  },
  saturday: {
    ...defaultDay,
    slots: [{ start: "", end: "" }],
    staffSlots: [{ start: "", end: "" }],
  },
  sunday: {
    ...defaultDay,
    slots: [{ start: "", end: "" }],
    staffSlots: [{ start: "", end: "" }],
  },
};

const TimePickerField: React.FC<{
  value: string;
  onChange: (val: string) => void;
  error?: boolean;
  errorMessage?: string;
}> = ({ value, onChange, error, errorMessage }) => (
  <div>
    <DatePicker
      selected={value ? new Date(`1970-01-01T${value}:00`) : null}
      onChange={(date) => {
        const formatted = date
          ? date.toLocaleTimeString("en-GB", {
              hour: "2-digit",
              minute: "2-digit",
            })
          : "";
        onChange(formatted);
      }}
      showTimeSelect
      showTimeSelectOnly
      timeIntervals={1}
      timeCaption="Time"
      dateFormat="HH:mm"
      timeFormat="HH:mm"
      className={`form-control ${error ? "is-invalid" : ""}`}
    />
    {error && errorMessage && (
      <div
        className="invalid-feedback position-absolute"
        style={{ fontSize: "0.75rem" }}
      >
        {errorMessage}
      </div>
    )}
  </div>
);

const SlotRow: React.FC<{
  dayKey: DayKey;
  slotIndex: number;
  control: any;
  required: boolean;
  t: any;
  onRemove?: () => void;
}> = ({ dayKey, slotIndex, control, required, t, onRemove }) => (
  <div className="row align-items-center mb-2">
    <div className="col-5">
      <Controller
        name={`${dayKey}.slots.${slotIndex}.start`}
        control={control}
        rules={
          required
            ? { required: t("minewClients:device.form.error.required") }
            : {}
        }
        render={({ field, fieldState }) => (
          <TimePickerField
            value={field.value}
            onChange={field.onChange}
            error={!!fieldState.error}
            errorMessage={fieldState.error?.message}
          />
        )}
      />
    </div>
    <div className="col-auto px-1">-</div>
    <div className="col-5">
      <Controller
        name={`${dayKey}.slots.${slotIndex}.end`}
        control={control}
        rules={
          required
            ? { required: t("minewClients:device.form.error.required") }
            : {}
        }
        render={({ field, fieldState }) => (
          <TimePickerField
            value={field.value}
            onChange={field.onChange}
            error={!!fieldState.error}
            errorMessage={fieldState.error?.message}
          />
        )}
      />
    </div>
    {onRemove && (
      <div className="col-auto px-1">
        <button
          type="button"
          className="btn btn-sm btn-outline-danger"
          onClick={onRemove}
        >
          &times;
        </button>
      </div>
    )}
  </div>
);

const DayRow: React.FC<{
  dayKey: DayKey;
  label: string;
  locationType: LocationType;
  control: any;
  watch: any;
  setValue: any;
  t: any;
}> = ({ dayKey, label, locationType, control, watch, setValue, t }) => {
  const closed = watch(`${dayKey}.closed`);
  const splitDay = watch(`${dayKey}.splitDay`);
  const slots = watch(`${dayKey}.slots`) || [];
  const staffPresent = watch(`${dayKey}.staffPresent`);
  const isDailySlots =
    locationType === "leven_in_zeebad" || locationType === "aqua_loop";
  const isGametown = locationType === "gametown";

  return (
    <div
      className="form-group row mb-2 pb-2"
      style={{ borderBottom: "1px solid #e9ecef" }}
    >
      <div className="col-2 d-flex align-items-center">
        <span className="font-weight-bold">
          {t(`minewClients:device:form.${label}`)}
        </span>
      </div>
      <div className="col-10">
        {/* Closed checkbox */}
        <div className="mb-1">
          <Controller
            name={`${dayKey}.closed`}
            control={control}
            render={({ field }) => (
              <div className="d-flex">
                <label
                  className="d-inline-flex align-items-center mb-0"
                  style={{ cursor: "pointer" }}
                >
                  <input
                    type="checkbox"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    className="mr-2"
                  />
                  {t("minewClients:device:form.closed")}
                </label>
              </div>
            )}
          />
        </div>

        {!closed && (
          <>
            {/* Split day toggle — only for default */}
            {!isDailySlots && !isGametown && (
              <div className="mb-2">
                <Controller
                  name={`${dayKey}.splitDay`}
                  control={control}
                  render={({ field }) => (
                    <div className="d-flex">
                      <label
                        className="d-inline-flex align-items-center mb-0"
                        style={{ cursor: "pointer" }}
                      >
                        <input
                          type="checkbox"
                          checked={field.value}
                          onChange={(e) => {
                            field.onChange(e.target.checked);
                            if (e.target.checked && slots.length < 2) {
                              setValue(`${dayKey}.slots`, [
                                slots[0] || { start: "", end: "" },
                                { start: "", end: "" },
                              ]);
                            }
                            if (!e.target.checked && slots.length > 1) {
                              setValue(`${dayKey}.slots`, [slots[0]]);
                            }
                          }}
                          className="mr-2"
                        />
                        {t("minewClients:device:form.splitDay")}
                      </label>
                    </div>
                  )}
                />
              </div>
            )}

            {/* Time slots */}
            {isDailySlots ? (
              <>
                {slots.map((_: any, idx: number) => (
                  <SlotRow
                    // eslint-disable-next-line react/no-array-index-key
                    key={idx}
                    dayKey={dayKey}
                    slotIndex={idx}
                    control={control}
                    required
                    t={t}
                    onRemove={
                      slots.length > 1
                        ? () => {
                            const newSlots = slots.filter(
                              (_: any, i: number) => i !== idx,
                            );
                            setValue(`${dayKey}.slots`, newSlots);
                          }
                        : undefined
                    }
                  />
                ))}
                {slots.length < 8 && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary mt-1"
                    onClick={() => {
                      setValue(`${dayKey}.slots`, [
                        ...slots,
                        { start: "", end: "" },
                      ]);
                    }}
                  >
                    + {t("minewClients:device:form.addSlot")}
                  </button>
                )}
              </>
            ) : (
              <div
                className="d-flex align-items-center flex-nowrap mb-2"
                style={{ gap: "6px" }}
              >
                {/* Opening hours time pickers */}
                <div style={{ width: 160, flexShrink: 0 }}>
                  <Controller
                    name={`${dayKey}.slots.0.start`}
                    control={control}
                    rules={{
                      required: t("minewClients:device.form.error.required"),
                    }}
                    render={({ field, fieldState }) => (
                      <TimePickerField
                        value={field.value}
                        onChange={field.onChange}
                        error={!!fieldState.error}
                        errorMessage={fieldState.error?.message}
                      />
                    )}
                  />
                </div>
                <span>-</span>
                <div style={{ width: 160, flexShrink: 0 }}>
                  <Controller
                    name={`${dayKey}.slots.0.end`}
                    control={control}
                    rules={{
                      required: t("minewClients:device.form.error.required"),
                    }}
                    render={({ field, fieldState }) => (
                      <TimePickerField
                        value={field.value}
                        onChange={field.onChange}
                        error={!!fieldState.error}
                        errorMessage={fieldState.error?.message}
                      />
                    )}
                  />
                </div>
                {splitDay && (
                  <>
                    <span>/</span>
                    <div style={{ width: 160, flexShrink: 0 }}>
                      <Controller
                        name={`${dayKey}.slots.1.start`}
                        control={control}
                        rules={{
                          required: t(
                            "minewClients:device.form.error.required",
                          ),
                        }}
                        render={({ field, fieldState }) => (
                          <TimePickerField
                            value={field.value}
                            onChange={field.onChange}
                            error={!!fieldState.error}
                            errorMessage={fieldState.error?.message}
                          />
                        )}
                      />
                    </div>
                    <span>-</span>
                    <div style={{ width: 160, flexShrink: 0 }}>
                      <Controller
                        name={`${dayKey}.slots.1.end`}
                        control={control}
                        rules={{
                          required: t(
                            "minewClients:device.form.error.required",
                          ),
                        }}
                        render={({ field, fieldState }) => (
                          <TimePickerField
                            value={field.value}
                            onChange={field.onChange}
                            error={!!fieldState.error}
                            errorMessage={fieldState.error?.message}
                          />
                        )}
                      />
                    </div>
                  </>
                )}

                {/* Gametown: staff availability inline after | */}
                {isGametown && (
                  <>
                    <span className="font-weight-bold">|</span>
                    <Controller
                      name={`${dayKey}.staffPresent`}
                      control={control}
                      render={({ field }) => (
                        <div className="d-flex">
                          <label
                            className="d-inline-flex align-items-center mb-0"
                            style={{ cursor: "pointer" }}
                          >
                            <input
                              type="checkbox"
                              checked={field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                              className="mr-1"
                            />
                            {t(
                              "minewClients:device:form.staffPresent",
                              "Staff present",
                            )}
                          </label>
                        </div>
                      )}
                    />
                    {staffPresent && (
                      <>
                        <div style={{ width: 160, flexShrink: 0 }}>
                          <Controller
                            name={`${dayKey}.staffSlots.0.start`}
                            control={control}
                            rules={{
                              required: t(
                                "minewClients:device.form.error.required",
                              ),
                            }}
                            render={({ field, fieldState }) => (
                              <TimePickerField
                                value={field.value}
                                onChange={field.onChange}
                                error={!!fieldState.error}
                                errorMessage={fieldState.error?.message}
                              />
                            )}
                          />
                        </div>
                        <span>-</span>
                        <div style={{ width: 160, flexShrink: 0 }}>
                          <Controller
                            name={`${dayKey}.staffSlots.0.end`}
                            control={control}
                            rules={{
                              required: t(
                                "minewClients:device.form.error.required",
                              ),
                            }}
                            render={({ field, fieldState }) => (
                              <TimePickerField
                                value={field.value}
                                onChange={field.onChange}
                                error={!!fieldState.error}
                                errorMessage={fieldState.error?.message}
                              />
                            )}
                          />
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const CenterParcsTemplateFields = React.memo(() => {
  const history = useHistory();
  const { t } = useTranslation(["minewClients", "common"]);
  const { minewId } = useParams<{ minewId: string }>();
  const [modal, setModal] = useState<"remove" | "force-update" | null>(null);
  const [iframeKey, setIframeKey] = useState<number>(0);

  const reloadIframe = () => setIframeKey((prev) => prev + 1);
  const initialLoadDone = React.useRef(false);

  const methods = useForm<FormValues>({
    shouldFocusError: false,
    shouldUnregister: false,
    mode: "onChange",
    defaultValues,
  });

  const { data: minewData, refetch } = useQuery<{ minew: IMinew }>(
    QUERY_MINEW_FILTERED,
    {
      skip: !minewId,
      variables: { id: minewId },
      fetchPolicy: "network-only",
    },
  );

  const previewUrl = useMemo(
    () => get(minewData, "minew.previewUrl"),
    [minewData],
  );
  const entityId = useMemo(
    () => minewData?.minew.items[0]?.entity.id,
    [minewData],
  );
  const templateName = useMemo(
    () => minewData?.minew.template || "",
    [minewData],
  );
  const locationType = useMemo(
    () => getLocationType(templateName),
    [templateName],
  );

  const { data, refetch: refetchEntity } = useQuery<{
    entity: IEntityWithOpeningHours;
  }>(QUERY_ENTITY_OPENING_HOURS, {
    skip: !entityId,
    variables: { id: entityId },
    fetchPolicy: "network-only",
    onCompleted: ({ entity: { properties } }) => {
      if (initialLoadDone.current) return;

      const prop = properties.find(
        // eslint-disable-next-line no-underscore-dangle
        (p: any) => p.__typename === "OpeningHours",
      );
      if (!prop) return;

      const formData: any = { title: prop.title || "" };

      DAY_KEYS.forEach((dayKey) => {
        const day = prop[dayKey];
        if (day) {
          formData[dayKey] = {
            closed: day.closed || false,
            splitDay: day.splitDay || false,
            slots: day.slots?.length
              ? day.slots.map((s) => ({
                  start: s.start || "",
                  end: s.end || "",
                }))
              : [{ start: "", end: "" }],
            staffPresent: day.staffAvailability?.present !== false,
            staffSlots: day.staffAvailability?.slots?.length
              ? day.staffAvailability.slots.map((s) => ({
                  start: s.start || "",
                  end: s.end || "",
                }))
              : [{ start: "", end: "" }],
          };
        }
      });

      methods.reset(formData);
      initialLoadDone.current = true;
    },
  });

  const openingHoursProp = data?.entity?.properties?.find(
    // eslint-disable-next-line no-underscore-dangle
    (p: any) => p.__typename === "OpeningHours",
  );
  const openingHoursId = openingHoursProp?.id;
  const openingHoursTitle = openingHoursProp?.title;

  const [onCreate] = useMutation(CREATE_OPENING_HOURS);
  const [onUpdate] = useMutation(UPDATE_OPENING_HOURS);
  const [onEntityAttachProperties] = useMutation(CREATE_ENTITY_PROPERTIES);
  const [onDelete, { loading: deleteLoading }] =
    useMutation(DELETE_OPENING_HOURS);
  const [onRestartMinew, { loading: restartLoading }] =
    useMutation(RESTART_MINEW);

  const onForceUpdate = useCallback(
    async (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.preventDefault();
      try {
        await onRestartMinew({ variables: { id: minewId } });
        toast.success<string>(t("Minew successfully restarted"));
      } catch (error) {
        toast.error<string>(
          t("minewClients:toast.forcedUpdateError", "Force update failed"),
        );
      }
      setModal(null);
    },
    [t, minewId, onRestartMinew],
  );

  const onRemove = useCallback(
    async (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.preventDefault();
      try {
        await onDelete({ variables: { id: openingHoursId } });
        toast.success<string>(t("minewClients:toast.updated"));
        history.replace(`/msi-devices`);
      } catch (error) {
        toast.error<string>(t("minewClients:toast.deletedError"));
      }
      setModal(null);
    },
    [onDelete, openingHoursId, openingHoursTitle, history, t],
  );

  const buildDayInput = (dayValues: DayFormValues) => ({
    closed: dayValues.closed,
    splitDay: dayValues.splitDay,
    slots: (dayValues.slots || []).filter((s) => s.start || s.end),
    staffAvailability: {
      present: dayValues.staffPresent ?? true,
      slots: (dayValues.staffSlots || []).filter((s) => s.start || s.end),
    },
  });

  const onSubmit = async (values: FormValues) => {
    const input: any = {
      title: values.title || templateName,
      locationType,
    };

    DAY_KEYS.forEach((dayKey) => {
      input[dayKey] = buildDayInput(values[dayKey]);
    });

    if (openingHoursId) {
      try {
        await onUpdate({
          variables: { input: { id: openingHoursId, ...input } },
        });
        toast.success<string>(t("minewClients:toast.updated"));
        await refetchEntity();
        await refetch();
        if (previewUrl) reloadIframe();
        await onRestartMinew({ variables: { id: minewId } });
      } catch (error) {
        toast.error<string>(
          t("minewClients:toast.updatedError", "Update failed"),
        );
      }
    } else {
      try {
        const { data: createData } = await onCreate({ variables: { input } });
        const newId = createData.createOpeningHours.id;

        await onEntityAttachProperties({
          variables: { input: { id: entityId, properties: [newId] } },
        });

        toast.success<string>(t("minewClients:toast.updated"));
        initialLoadDone.current = true;
        await refetchEntity();
        await refetch();
        if (previewUrl) reloadIframe();
        await onRestartMinew({ variables: { id: minewId } });
      } catch (error) {
        toast.error<string>(
          t("minewClients:toast.createdError", "Create failed"),
        );
      }
    }
  };

  return (
    <div className="container-fluid template-fields">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="row">
            <div className="col-xl-8 mt-5">
              {DAY_KEYS.map((dayKey, idx) => (
                <DayRow
                  key={dayKey}
                  dayKey={dayKey}
                  label={DAY_LABELS[idx]}
                  locationType={locationType}
                  control={methods.control}
                  watch={methods.watch}
                  setValue={methods.setValue}
                  t={t}
                />
              ))}
            </div>
            <div className="col-xl-4 pb-3">
              <p className="mb-4">
                {t("minew:minew.minewForm.form.preview.h3")} (
                {t("wayfinding:wayfinding.wayfindingForm.form.preview.notice")})
              </p>
              <iframe
                key={iframeKey}
                scrolling="no"
                title="preview"
                src={previewUrl}
                style={{
                  border: "1px solid #ced4da",
                  borderRadius: "0.25rem",
                  width: 390 / (3 / 4),
                  height: 390,
                  transform: "rotate(90deg) translateY(-100%)",
                  transformOrigin: "top left",
                  display: "block",
                }}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <input type="submit" className="btn btn-primary" />
              {openingHoursId && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    setModal("remove");
                  }}
                  className="btn btn-danger ml-3"
                >
                  {t("common:delete")}
                </button>
              )}
              <button
                className="btn btn-secondary ml-3"
                onClick={(e) => {
                  e.preventDefault();
                  setModal("force-update");
                }}
              >
                {t("minewClients:device:form.forceUpdate", "Force update")}
              </button>
            </div>
          </div>
        </form>
      </FormProvider>
      <Modal
        show={!!modal}
        onHide={() => setModal(null)}
        backdrop="static"
        centered
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {modal === "remove"
              ? t("minewClients:modal.remove.title", "Delete opening hours")
              : t("minewClients:modal.forceUpdate.title", "Force update")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modal === "remove"
            ? t(
                "minewClients:modal.remove.body",
                "Are you sure you want to delete these opening hours?",
              )
            : t(
                "minewClients:modal.forceUpdate.body",
                "Are you sure you want to force update this device?",
              )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setModal(null)}>
            {t("common:cancel")}
          </Button>
          <Button
            variant="danger"
            disabled={restartLoading || deleteLoading}
            onClick={(e) => {
              if (modal === "force-update") onForceUpdate(e);
              if (modal === "remove") onRemove(e);
            }}
          >
            {modal === "remove" && t("common:delete")}
            {modal === "force-update" && t("common:restart", "Restart")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
});

export default CenterParcsTemplateFields;
